import type { CalendarEventViewDto } from '@/types/calendarEvent'

export function getCalendarIdForType(type: string): string {
  switch (type) {
    case 'Email':
      return 'email'
    case 'Phone':
      return 'phone'
    case 'Social':
      return 'social'
    case 'Online Event':
      return 'online-event'
    case 'Launch Date':
      return 'launch-date'
    default:
      return 'default'
  }
}

export function convertEventsToScheduleX(
  events: CalendarEventViewDto[],
  getTimezoneDisplayName?: (ianaId: string) => string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] {
  return events.map(event => {
    // Determine if this is a timed event or all-day event
    // All-day if: IsFullDay is true OR missing EndDate
    const isAllDay = event.IsFullDay || !event.EndDate

    // Email touchpoints carry a structured EmailSendRule, not a single
    // ScheduledDate. There's no meaningful translation from a calendar drag
    // back to the rule, so we lock these events from drag-and-drop and resize.
    const isLockedFromDrag = event.Type === 'Email'
    
    try {
      if (!isAllDay && event.EndDate) {
        // Timed event with start and end times
        // The server sends DateTimeOffset (e.g., 2026-01-13T13:00:00-05:00)
        // This represents an absolute moment in time
        // Schedule-X uses epochMilliseconds and displays times in UTC
        // So we need to give it a time that LOOKS like the local time but in UTC
        
        // Get the viewer's local timezone
        const viewerTimeZone = globalThis.Temporal.Now.timeZoneId()
        
        // Convert to viewer's timezone to get the local time components
        const startInstant = globalThis.Temporal.Instant.from(event.StartDate.trim())
        const endInstant = globalThis.Temporal.Instant.from(event.EndDate.trim())
        
        const startLocal = startInstant.toZonedDateTimeISO(viewerTimeZone)
        const endLocal = endInstant.toZonedDateTimeISO(viewerTimeZone)
        
        // Create a ZonedDateTime in UTC with the SAME hour/minute as the local time
        // This tricks Schedule-X into displaying the correct local time
        const startUTC = globalThis.Temporal.ZonedDateTime.from({
          year: startLocal.year,
          month: startLocal.month,
          day: startLocal.day,
          hour: startLocal.hour,
          minute: startLocal.minute,
          second: startLocal.second,
          timeZone: 'UTC',
        })
        
        const endUTC = globalThis.Temporal.ZonedDateTime.from({
          year: endLocal.year,
          month: endLocal.month,
          day: endLocal.day,
          hour: endLocal.hour,
          minute: endLocal.minute,
          second: endLocal.second,
          timeZone: 'UTC',
        })
        
        // Title format: "Type: Title - Full Timezone Name"
        let title = `${event.Type}: ${event.Title}`
        
        // Add timezone display name to title with dash separator
        if (event.TimeZone && getTimezoneDisplayName) {
          const tzDisplayName = getTimezoneDisplayName(event.TimeZone)
          if (tzDisplayName) {
            title = `${title} - ${tzDisplayName}`
          }
        }
        
        // Keep description as-is (no longer prepending timezone)
        const description = event.Description || ''
        
        return {
          id: event.Id,
          title,
          start: startUTC,
          end: endUTC,
          calendarId: getCalendarIdForType(event.Type),
          description,
          _etag: event.SourceETag,
          _timeZone: event.TimeZone,
          _options: {
            additionalClasses: [isLockedFromDrag ? 'locked-event' : 'draggable-event'],
            disableDND: isLockedFromDrag,
            disableResize: isLockedFromDrag,
          },
        }
      } else {
        // All-day events use PlainDate
        const dateOnly = event.StartDate.split('T')[0]
        
        return {
          id: event.Id,
          title: `${event.Type}: ${event.Title}`,
          start: globalThis.Temporal.PlainDate.from(dateOnly),
          end: globalThis.Temporal.PlainDate.from(dateOnly),
          calendarId: getCalendarIdForType(event.Type),
          description: event.Description || undefined,
          _etag: event.SourceETag,
          _options: {
            additionalClasses: [isLockedFromDrag ? 'locked-event' : 'draggable-event'],
            disableDND: isLockedFromDrag,
            disableResize: isLockedFromDrag,
          },
        }
      }
    } catch (conversionError) {
      console.error('Error converting event:', event, conversionError)
      const today = globalThis.Temporal.Now.plainDateISO()
      return {
        id: event.Id,
        title: `${event.Type}: ${event.Title} (Error)`,
        start: today,
        end: today,
        calendarId: getCalendarIdForType(event.Type),
      }
    }
  })
}
